import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, Copy, Check, Users, Briefcase } from 'lucide-react';

const INTERNAL_GUIDE = `# BrandStage Internal Operations Guide
### Team Reference Document | August 2026

---

## Quick Reference: Admin Access

**Admin Dashboard URL:** https://brandstage-media.com/admin
**Admin Login:** Use your authorized email (must be in ADMIN_EMAILS list)
**Default Admin:** brandstagevideo@gmail.com

---

## 1. Admin Dashboard Overview

The Admin Dashboard is your central command center. Access it by logging in and navigating to /admin. The dashboard has 10 tabs: Overview, Reviews, All Videos, Bookings, Events, Profiles, Leaders, Invoices, Coupons, and Audit & Export. The Overview tab shows real-time statistics including pending reviews, today's submissions, pending payments, total revenue, and active events. Use the Refresh button in the top-right corner to update all data.

---

## 2. Video Submission Management

### Reviewing Free User Submissions
Free users' video recordings go to a review queue before branding. Navigate to the **Reviews tab** to see pending submissions. Each card shows the user's name, company, email, and a video preview. Click **Approve** to start the branding process (video will be processed with intro/outro and sent to the user), or click **Reject** with a reason (user receives notification). Paid/Trade Show users bypass this queue entirely—their videos are auto-branded immediately.

### Viewing All Videos
The **All Videos tab** shows every submission regardless of status. You can see branding status (pending, processing, complete, failed), watch videos, and take actions. Use the checkboxes for bulk deletion. If a video is stuck on "processing" for more than 15 minutes, it has likely failed—use the Reset button to clear the status and try again.

### Adding Branding Manually
For videos without branding, click the **Wand icon** (Add Branding) on any submission. A modal appears with two options: **BrandStage Branding** (uses our standard intro/outro with background music) or **Client's Custom Branding** (upload the client's own intro and/or outro MP4 files). Custom branding is useful for white-label clients who want their own identity on videos.

---

## 3. Company Profile Management

### Creating Profiles (Admin on behalf of clients)
Navigate to /profile/create while logged in as admin. The 5-step wizard guides you through: **Basics** (company name, logo, location) → **Objective** (category like Business Expansion, Investment Invitation; target audience; key message) → **Details** (certifications, key markets, products) → **Media** (YouTube videos, images) → **Preview**. The AI generates structured content based on your inputs. Check "Created on behalf of" and enter the client company name for agency attribution.

### Editing Existing Profiles
From the **Profiles tab** in Admin Dashboard, click **Edit** on any profile. You can modify all fields including AI-generated sections. Premium users can click "Regenerate All" to refresh AI content based on updated inputs. Changes save immediately.

### Key Contact Feature
During profile creation, you can add a Key Contact person—this creates a personal profile within the company profile showing photo, bio, expertise areas, achievements, and LinkedIn. The AI generates a professional bio based on their background. This is excellent for featuring the CEO/Founder prominently.

### Public Profile URLs
Each profile gets a shareable URL: https://brandstage-media.com/p/company-slug. Share this with clients for review or send to their prospects. The page is mobile-responsive and includes WhatsApp, LinkedIn, and Copy Link share buttons.

---

## 4. Leadership Profile Management

### Creating Leadership Profiles
Navigate to /leadership/create or click **Create Leadership Profile** in the Leaders tab. The 7-step wizard covers: **Mode** (Company Leadership vs Leadership Identity) → **Basics** (name, photo, title) → **Company Role/Career** → **Expertise** (tags and industries) → **Vision/Thought Leadership** → **Contact** → **Preview**. Choose "Company Leadership" mode for executives within organizations, or "Leadership Identity" for thought leaders building personal brands.

### Editing Leadership Profiles
From the **Leaders tab** in Admin Dashboard, click **Edit** to modify any profile. You can change all fields, update the photo, add/remove expertise tags, and regenerate AI sections. The edit page shows a "View Live" button to preview changes.

### Leadership vs Company Profiles
Leadership Profiles (/l/slug) focus on individuals—their journey, expertise, vision. Company Profiles (/p/slug) focus on organizations—products, markets, partnerships. A leader can have both: a personal Leadership Profile linked to their Company Profile. This dual presence is powerful for investor relations and B2B contexts.

---

## 5. Booth Recording System (Kiosk Mode)

### Setting Up an Event
Before a trade show, create an Event in the **Events tab**. Click **Create Event**, enter event name, date range, and recording limit (max videos allowed). The system generates a unique **Access Code** (e.g., "EXPO2026"). Share this code with booth operators.

### Operating the Kiosk
Access kiosk mode at /booth. Enter the event access code to begin. The system shows a lead capture form (Name, Designation, Company, Email, Mobile, Purpose). After form submission, the user sees a 5-point declaration with checkboxes covering authority, accuracy, prohibited content, rights, and indemnification—all must be checked to proceed. Recording starts with a 45-second limit; a "Wrap up!" warning appears at 40 seconds. After recording, the video uploads automatically and the thank you screen appears.

### Processing Flow
Trade show recordings are auto-branded (no review needed) and emails are sent to both the user and admin (brandstagevideo@gmail.com) with download links. If branding fails, the original video is sent as fallback. Monitor the All Videos tab to confirm branding completion.

---

## 6. Booth Booking System

### How Clients Book
Clients visit /book-booth to request booth services. The multi-step form collects: Package selection (Recording or Editing Suite) → Customer details → Terms & Conditions acceptance → 5-point declaration → Submission. All legal acceptances are stored with timestamps and IP addresses for compliance.

### Confirming Payments
Bookings appear in the **Bookings tab** with "Pending Payment" status. When payment is received (bank transfer, UPI, etc.), click the booking, enter payment details (amount, method, reference), and click **Confirm Payment**. The system generates a unique event code and sends it to the customer automatically.

### Audit Records
Each booking has a **View Record** button that generates a PDF containing all legal acceptances, timestamps, IP addresses, and payment details. Use this for compliance documentation or disputes.

---

## 7. Invoice Management

### Creating Invoices
Go to the **Invoices tab** and click **Create Invoice**. Select brand (BrandStage, Continex, or TradeVesta)—each has different letterhead and bank details. Enter client information including K/A contact person. Add line items with descriptions, quantities, and INR rates. Toggle GST for domestic invoices (18% auto-calculated). For international invoices, enable dual currency and set exchange rate.

### Invoice Numbers
Choose manual entry or auto-generate. Auto-generated numbers follow the format: PREFIX-YEAR-RANDOM (e.g., BS-2026-7A3F). The prefix depends on selected brand.

### Sharing Invoices
Each invoice has a **Shareable Link** that clients can view without logging in. Click the link icon to copy. For bulk sharing (monthly reports to accountants), use **Export for Accountant** to copy all links for a selected period.

### PDF Download
Click the PDF icon to download a professional invoice matching the Continex template format with proper letterhead, terms, and bank details.

---

## 8. Coupon System

### Creating Promotional Coupons
In the **Coupons tab**, click **Create Coupon**. Enter a code (auto-generated or custom), select discount type (Free/100% off, Percentage, Fixed Amount), set optional usage limits and expiry date. Coupons can be used for profile upgrades.

### Managing Coupons
Toggle coupons active/inactive using the switch icon. View usage statistics (times used vs max uses). Delete coupons that are no longer needed. Inactive coupons are grayed out in the list.

---

## 9. Audit & Compliance

### Exporting Data
The **Audit & Export tab** provides one-click CSV downloads for: Users, Video Submissions, Bookings, Company Profiles, Leadership Profiles, and the complete Audit Log. Use these for compliance audits, data backups, or analysis.

### Consent & Declaration Log
Click **Load Audit Log** to view all consent records in a table. Each entry shows: Type (terms acceptance, video declaration, submission declaration, booking legal acceptance), User details, Timestamp, Details of what was accepted, and IP Address. This is your compliance proof that users consented to terms before using the platform.

### What's Tracked
- **Terms Acceptance**: When users register and accept T&C
- **Video Declaration**: When users accept the 5-point recording declaration
- **Submission Declaration**: Each video submission's declaration status
- **Booking Legal Acceptance**: Full audit trail for booth bookings

---

## 10. User Management

### User Registration
Users register at /register with email, name, company, and password. They must accept Terms & Conditions (checkbox). Registration triggers a welcome flow. Users start on the "free" tier with limited recordings.

### Password Reset
Users can reset passwords via /forgot-password. They enter their email, receive a reset link (valid 1 hour), and set a new password at /reset-password?token=xxx.

### Subscription Tiers
Users have tiers: free, professional, business. Tier affects recording limits and feature access. Tier upgrades are currently manual—contact admin to change a user's tier in the database.

---

## 11. Email Notifications

### Automatic Emails Sent
- **Welcome email**: On registration
- **Video ready email**: When branding completes (to user AND admin)
- **Original video fallback**: If branding fails (to user AND admin)
- **Booking confirmation**: When payment is confirmed with event code
- **Contact form submissions**: Forwarded to brandstagevideo@gmail.com

### Email Provider
All emails are sent via Resend. Check the Resend dashboard for delivery logs if emails aren't arriving.

---

## 12. Troubleshooting Common Issues

### Video Stuck on "Processing"
If a video shows "processing" for more than 15 minutes, the branding task likely failed silently. Go to All Videos, find the submission, and use the Reset button to clear the status. Then click Add Branding to retry. Check backend logs if it fails repeatedly.

### Login Issues
Ensure the email is registered. For admin access, the email must be in the ADMIN_EMAILS list in the backend configuration. Password issues can be resolved via Forgot Password flow.

### Profile Not Showing
Profiles are public immediately after creation. If a profile doesn't appear at its URL, check that the slug is correct (no special characters, lowercase). Try clearing browser cache.

### CSV Export Not Downloading
Ensure you're logged in as admin. The export endpoints require authentication. If the download starts but produces an empty file, there may be no data in that collection.

---

## Quick Links

| Function | URL |
|----------|-----|
| Admin Dashboard | /admin |
| Create Company Profile | /profile/create |
| Create Leadership Profile | /leadership/create |
| Kiosk Mode | /booth |
| Book Booth | /book-booth |
| Login | /login |
| Register | /register |

---

*Document Version: 1.0 | Last Updated: August 2026*`;

const CUSTOMER_NOTE = `# Welcome to BrandStage
## Your Leadership Story, Amplified

---

### The Video-First Era is Here

We are witnessing a fundamental shift in how businesses communicate, build trust, and create lasting impressions. In a world saturated with text and static content, **video has emerged as the most powerful medium for authentic connection**. Research consistently shows that viewers retain 95% of a message when they watch it in video, compared to just 10% when reading text. More importantly, video creates an emotional resonance that no brochure, website, or email can replicate.

For leaders and organisations seeking to expand into new markets, attract investment, or forge strategic partnerships, **the ability to tell your story compellingly on video is no longer optional—it's essential**.

This is where BrandStage comes in.

---

## What is BrandStage?

BrandStage is a comprehensive platform designed to help business leaders and organisations **create, manage, and share professional video content and digital profiles** that showcase their story, vision, and value proposition.

We believe that every leader has a compelling narrative. Every company has a unique journey worth sharing. Our mission is to make it effortless for you to capture and communicate these stories in formats that resonate with today's audiences—whether you're speaking to potential investors in Singapore, partners in Germany, or customers across India.

---

## Our Services

### 1. Professional Business Profiles

**Your Company's Digital Business Card—Reimagined**

Create a comprehensive, shareable profile for your organisation that goes far beyond a traditional website. Our AI-assisted profile builder helps you articulate:

- **Who You Are**: Your company's story, heritage, and core identity
- **Your Presence**: Markets served, clients, products, certifications
- **What You're Seeking**: Whether it's investment, partnerships, distributors, or expansion
- **Why Partner With You**: Your unique value proposition and competitive advantages

Each profile includes a **Key Contact section** featuring your leadership team with professional bios, expertise areas, and featured videos. Share your profile via a clean, mobile-friendly URL perfect for WhatsApp, LinkedIn, or email introductions.

**Perfect for**: Trade show follow-ups, investor decks, export inquiries, partnership proposals.

---

### 2. Leadership Profiles

**Your Professional Story, Professionally Told**

As a leader, your personal brand is inseparable from your organisation's brand. Our Leadership Profile feature creates a dedicated digital presence for executives and thought leaders, showcasing:

- **Your Journey**: Career milestones, pivotal decisions, lessons learned
- **Your Expertise**: Areas of specialisation, industries served, skills honed
- **Your Vision**: Where you see your industry heading and your role in shaping it
- **Your Voice**: Thought leadership topics, publications, speaking engagements

Whether you're a CEO building investor confidence, a founder sharing your entrepreneurial journey, or an industry expert establishing thought leadership, your Leadership Profile becomes your always-on professional introduction.

**Perfect for**: LinkedIn enhancement, conference introductions, board presentations, media kits.

---

### 3. Quick Bites Video Interviews

**45 Seconds That Can Change Everything**

In an age of shrinking attention spans, the ability to deliver a powerful message quickly is invaluable. Our Quick Bites feature enables you to record short, impactful video messages directly from your browser or at our trade show kiosks.

**How It Works**:
1. Answer a focused prompt (e.g., "What problem does your company solve?")
2. Record your response in 45 seconds or less
3. We add professional branding—intro, outro, and subtle background music
4. Receive your polished video ready for sharing

These bite-sized videos are perfect for:
- **Email signatures**: A video introduction that recipients actually watch
- **Social media**: Native content optimised for LinkedIn, WhatsApp, and Twitter
- **Website embedding**: Dynamic content that brings your homepage to life
- **Trade show follow-ups**: Personalised thank-you videos that stand out

**The BrandStage Advantage**: Unlike selfie videos shot on phones, Quick Bites are professionally framed, branded, and formatted—elevating your personal brand while maintaining authenticity.

---

### 4. Trade Show Kiosk Experience

**Capture the Energy of the Moment**

Trade shows and exhibitions are where deals begin. Our Trade Show Kiosk service brings professional video capture directly to your booth or event, allowing:

- **Visitor testimonials**: Capture genuine reactions and endorsements on-site
- **Product demonstrations**: Quick video showcases of your offerings
- **Partnership announcements**: Record joint statements with new partners
- **Leadership messages**: CEO updates filmed in the buzz of the event

All recordings are instantly processed and delivered to participants, creating immediate shareable content while the excitement is fresh.

---

## Getting Started: Create Your Profile

We invite you to experience BrandStage by creating your first profile. The process takes approximately 15-20 minutes and requires no technical expertise.

### Step 1: Register
Visit **brandstage-media.com/register** and create your account.

### Step 2: Choose Your Profile Type
- **Company Profile**: For your organisation's story
- **Leadership Profile**: For your personal professional brand

### Step 3: Follow the Guided Wizard
Our intuitive wizard walks you through each section. Answer the prompts naturally—our AI helps transform your inputs into polished, professional content.

### Step 4: Review and Publish
Preview your profile, make any adjustments, and publish. You'll receive a shareable URL instantly.

### Step 5: Share and Connect
Use your new profile URL in email signatures, LinkedIn posts, WhatsApp messages, and trade show conversations.

---

## Why Video-First Matters for Leadership

In the traditional business world, leaders were known through their actions, reputation, and perhaps the occasional industry publication. Today, **visibility is leadership currency**. The executives who thrive are those who can:

- **Communicate vision clearly**: Video forces clarity and authenticity
- **Build trust at scale**: One video can reach thousands while maintaining personal connection
- **Differentiate from competitors**: While others send PDFs, you send presence
- **Adapt to global audiences**: Video transcends language barriers with visual storytelling

Consider this: When an investor evaluates two similar opportunities, they will remember the founder whose video made them *feel* something. When a potential partner chooses between vendors, they will lean toward the team they've "met" through video. When a customer decides where to place their trust, they will choose the company whose leadership they can see, hear, and relate to.

**BrandStage gives you the tools to be that memorable leader.**

---

## Our Commitment to You

**Professional Quality**: Every piece of content that bears the BrandStage treatment meets broadcast-ready standards.

**Authentic Voice**: We enhance, not replace, your natural communication style. The words are yours; the polish is ours.

**Privacy & Control**: Your content remains yours. Share it widely or keep it private—you decide.

**Continuous Innovation**: As video technology evolves, so do we. Your BrandStage profile grows with the platform.

---

## Ready to Begin?

The leaders of tomorrow are building their digital presence today. We invite you to join them.

**Create your profile**: brandstage-media.com/register

**Questions?** Contact us at brandstagevideo@gmail.com

**See examples**: Visit brandstage-media.com to explore sample profiles and videos.

---

*BrandStage is a product of BrandStage Media, bringing 19+ years of video production expertise and having worked with 300+ business leaders across industries.*

---

### Quick Reference

| Service | Best For | Time Required |
|---------|----------|---------------|
| Company Profile | Organisations seeking partnerships, investment, or market expansion | 15-20 minutes |
| Leadership Profile | Executives building personal brand and thought leadership | 10-15 minutes |
| Quick Bites Video | Anyone needing short, professional video content | 5 minutes |
| Trade Show Kiosk | Events, exhibitions, conferences | On-site service |

---

*We look forward to helping you tell your story.*

**BrandStage Media**
*Leadership Narratives | Video First*`;

export default function AdminDocuments() {
  const navigate = useNavigate();
  const [activeDoc, setActiveDoc] = useState('internal');
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const text = activeDoc === 'internal' ? INTERNAL_GUIDE : CUSTOMER_NOTE;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const currentDoc = activeDoc === 'internal' ? INTERNAL_GUIDE : CUSTOMER_NOTE;

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Header */}
      <div className="bg-zinc-900 border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/admin')}
                className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                Back to Admin
              </button>
              <div className="h-6 w-px bg-zinc-700" />
              <h1 className="text-xl font-bold flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#C41E3A]" />
                Documentation
              </h1>
            </div>
            <button
              onClick={handleCopy}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                copied 
                  ? 'bg-green-600 text-white' 
                  : 'bg-[#C41E3A] hover:bg-[#A01830] text-white'
              }`}
              data-testid="copy-document-btn"
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  Copy to Clipboard
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Document Tabs */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="flex gap-4 mb-6">
          <button
            onClick={() => setActiveDoc('internal')}
            className={`flex items-center gap-3 px-6 py-4 rounded-xl border transition-colors ${
              activeDoc === 'internal'
                ? 'bg-[#C41E3A]/20 border-[#C41E3A] text-white'
                : 'bg-zinc-900/50 border-zinc-800 text-zinc-400 hover:border-zinc-700'
            }`}
            data-testid="internal-guide-tab"
          >
            <Users className="w-6 h-6" />
            <div className="text-left">
              <div className="font-semibold">Internal Operations Guide</div>
              <div className="text-sm opacity-70">For your team</div>
            </div>
          </button>
          
          <button
            onClick={() => setActiveDoc('customer')}
            className={`flex items-center gap-3 px-6 py-4 rounded-xl border transition-colors ${
              activeDoc === 'customer'
                ? 'bg-[#C41E3A]/20 border-[#C41E3A] text-white'
                : 'bg-zinc-900/50 border-zinc-800 text-zinc-400 hover:border-zinc-700'
            }`}
            data-testid="customer-note-tab"
          >
            <Briefcase className="w-6 h-6" />
            <div className="text-left">
              <div className="font-semibold">Customer Welcome Note</div>
              <div className="text-sm opacity-70">For prospects</div>
            </div>
          </button>
        </div>

        {/* Document Content */}
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-8">
          <pre className="whitespace-pre-wrap font-sans text-sm text-zinc-300 leading-relaxed">
            {currentDoc}
          </pre>
        </div>
      </div>
    </div>
  );
}
